import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GroceryItemData } from "./GroceryItem";
import { LoadingStatus } from "./LoadingStatus";

interface SwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentItem: GroceryItemData | null;
  alternatives: GroceryItemData[];
  onSwap: (newItemId: string) => void;
  isLoading?: boolean;
}

export const SwapModal = ({
  isOpen,
  onClose,
  currentItem,
  alternatives,
  onSwap,
  isLoading = false,
}: SwapModalProps) => {
  if (!currentItem) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-3xl max-h-[80vh] overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  Swap {currentItem.name}
                </h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {isLoading ? (
                <LoadingStatus type="swap" />
              ) : (
                <>
                  <p className="text-muted-foreground mb-6">
                    These alternatives provide similar nutrients:
                  </p>

                  <div className="space-y-3">
                    {alternatives.map((alt, index) => (
                      <motion.button
                        key={alt.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => onSwap(alt.id)}
                        className="w-full p-4 rounded-2xl bg-card border border-border text-left hover:border-primary transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-foreground">{alt.name}</h3>
                            <p className="text-sm text-muted-foreground">{alt.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-metabolic-500">
                              {alt.score}% match
                            </span>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </>
              )}

              <Button
                onClick={onClose}
                variant="ghost"
                className="w-full mt-6 h-12 text-muted-foreground"
              >
                Keep current item
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
